import React, { useCallback } from "react";
import { isNil } from "ramda";

import { Field, FieldGroup, FieldLabel, ValidationError } from "affinityUI/form/components";
import { Input } from "affinityUI/form/inputs/input";
import { toNumber } from "lib/helpers";

import { useAffinityField } from "../../helpers";

const validateRange = (value?: Maybe<{ min: number; max: number }>) =>
  !isNil(value) && value.min >= value.max ? "Min value should be lower than max" : undefined;

export const RangeField: React.FC<
  React.PropsWithChildren<{ name: string; value?: Maybe<{ min: string | number; max: string | number }> }>
> = ({ children, ...props }) => {
  const { name } = props;
  const [{ value = {} }, meta, { setValue, setError }] = useAffinityField({ name, validate: validateRange });

  const updateValue = useCallback(
    (rawValue: { min?: string; max?: string }) => {
      setError(undefined);
      const value = { min: toNumber(rawValue.min), max: toNumber(rawValue.max) };
      if (isNil(value.min) && isNil(value.max)) {
        setValue(undefined);
        return;
      }
      setValue(value);
    },
    [setError, setValue]
  );

  const handleMinOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateValue({ ...(value ?? {}), min: e.target.value });
    },
    [value, updateValue]
  );
  const handleMaxOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateValue({ ...(value ?? {}), max: e.target.value });
    },
    [value, updateValue]
  );

  return (
    <Field role="group" aria-labelledby={name}>
      <FieldLabel id={name} htmlFor={name}>
        {children}
      </FieldLabel>
      <FieldGroup>
        <FieldLabel visuallyHidden htmlFor={`${name}.min`}>
          Minimum value
        </FieldLabel>
        <Input
          type="number"
          value={value?.min ?? ""}
          id={`${name}.min`}
          name={`${name}.min`}
          onChange={handleMinOnChange}
          $error={meta.error}
          data-testid={`${name}.min`}
        />
        <span>to</span>
        <FieldLabel visuallyHidden htmlFor={`${name}.max`}>
          Maximum value
        </FieldLabel>
        <Input
          type="number"
          value={value?.max ?? ""}
          id={`${name}.max`}
          name={`${name}.max`}
          onChange={handleMaxOnChange}
          $error={meta.error}
          data-testid={`${name}.min`}
        />
      </FieldGroup>
      {meta?.error && <ValidationError>{meta?.error}</ValidationError>}
    </Field>
  );
};
